/**
 * Shopkeepers API
 * Handles shop management, profile updates, and shop discovery
 */

import { apiClient, handleApiResponse, handleApiError } from '../config';
import {
  Shopkeeper,
  ShopkeeperUpdate,
  ShopSearch,
  NearbyShop,
  ShopStats,
  PaginatedResponse,
} from '../../types';

export class ShopkeepersAPI {
  /**
   * Get shopkeeper profile
   */
  static async getProfile(shopkeeperId?: number): Promise<Shopkeeper> {
    try {
      const url = shopkeeperId ? `/api/v1/shopkeepers/${shopkeeperId}/` : '/api/v1/shopkeepers/profile/';
      const response = await apiClient.get(url);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update shopkeeper profile
   */
  static async updateProfile(updates: ShopkeeperUpdate): Promise<Shopkeeper> {
    try {
      const response = await apiClient.put('/api/v1/shopkeepers/profile/', updates);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update shop status (open/closed)
   */
  static async updateStatus(isOpen: boolean, isAccepting?: boolean): Promise<Shopkeeper> {
    try {
      const response = await apiClient.post('/api/v1/shopkeepers/status/', {
        is_open: isOpen,
        is_currently_accepting: isAccepting,
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Search nearby shops
   */
  static async searchShops(searchParams: ShopSearch): Promise<NearbyShop[]> {
    try {
      const params = new URLSearchParams();

      if (searchParams.city) params.append('city', searchParams.city);
      if (searchParams.latitude) params.append('latitude', searchParams.latitude.toString());
      if (searchParams.longitude) params.append('longitude', searchParams.longitude.toString());
      if (searchParams.radius) params.append('radius', searchParams.radius.toString());
      if (searchParams.business_category) params.append('category', searchParams.business_category);
      if (searchParams.is_open !== undefined) params.append('is_open', searchParams.is_open.toString());

      const response = await apiClient.get(`/api/v1/public/shops/?${params.toString()}`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get shop info by QR code ID
   */
  static async getShopByQRCode(qrCodeId: string): Promise<Shopkeeper> {
    try {
      const response = await apiClient.get(`/api/v1/public/shops/qr/${qrCodeId}/`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get shopkeeper documents
   */
  static async getDocuments(shopkeeperId: number, params: any = {}): Promise<PaginatedResponse<any>> {
    try {
      const searchParams = new URLSearchParams(params);
      const response = await apiClient.get(`/api/v1/shopkeepers/${shopkeeperId}/documents/?${searchParams.toString()}`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get shopkeeper statistics
   */
  static async getStats(shopkeeperId?: number): Promise<ShopStats> {
    try {
      const url = shopkeeperId
        ? `/api/v1/shopkeepers/${shopkeeperId}/stats/`
        : '/api/v1/shopkeepers/stats/';
      const response = await apiClient.get(url);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Generate new QR code
   */
  static async regenerateQRCode(): Promise<Shopkeeper> {
    try {
      const response = await apiClient.post('/api/v1/shopkeepers/regenerate-qr/');
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update shop location
   */
  static async updateLocation(latitude: number, longitude: number): Promise<Shopkeeper> {
    try {
      const response = await apiClient.post('/api/v1/shopkeepers/location/', {
        latitude,
        longitude,
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update shop schedule
   */
  static async updateSchedule(schedule: {
    open_time?: string;
    close_time?: string;
    days_open?: string;
    break_time_start?: string;
    break_time_end?: string;
  }): Promise<Shopkeeper> {
    try {
      const response = await apiClient.post('/api/v1/shopkeepers/schedule/', schedule);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update pricing
   */
  static async updatePricing(pricing: {
    price_per_page_bw?: number;
    price_per_page_color?: number;
    min_order_value?: number;
  }): Promise<Shopkeeper> {
    try {
      const response = await apiClient.post('/api/v1/shopkeepers/pricing/', pricing);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get all shopkeepers (admin only)
   */
  static async getAllShopkeepers(params: any = {}): Promise<PaginatedResponse<Shopkeeper>> {
    try {
      const searchParams = new URLSearchParams(params);
      const response = await apiClient.get(`/api/v1/shopkeepers/?${searchParams.toString()}`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Verify shopkeeper (admin only)
   */
  static async verifyShopkeeper(shopkeeperId: number): Promise<Shopkeeper> {
    try {
      const response = await apiClient.post(`/api/v1/shopkeepers/${shopkeeperId}/verify/`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

// Export default instance for convenience
export const shopkeepersAPI = ShopkeepersAPI;